import React from 'react';
import {
  AttributeDataType,
  type ListingCategoryAttribute,
  type ListingAttributeValue,
} from '../types';

// ─────────────────────────────────────────────────────────────────────────────
//  ListingAttributeFields
//  The category-specific part of the create form. Every field the category defines is
//  rendered and is editable by hand — nothing here is AI-only or read-only. Values the AI
//  filled are marked until the seller touches them; touching one clears the mark and locks
//  it against any later "Fill remaining with AI" run.
//
//  Used by both Bazaar listings and Classified ads: the only difference between them is
//  which definitions get passed in.
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  definitions: ListingCategoryAttribute[];
  values: Record<string, string>;
  /** Keys whose current value came from the AI and has not been edited since. */
  aiFilledKeys: Set<string>;
  onChange: (key: string, value: string) => void;
  loading?: boolean;
  /** Shown against fields the server rejected, keyed by attrKey. */
  errors?: Record<string, string>;
}

/** Converts the form's key→value map into the API's attribute array, dropping blanks. */
export function toAttributeValues(
  values: Record<string, string>,
  aiFilledKeys: Set<string>,
): ListingAttributeValue[] {
  return Object.entries(values)
    .filter(([, value]) => value != null && String(value).trim() !== '')
    .map(([key, value]) => ({ key, value: String(value).trim(), aiFilled: aiFilledKeys.has(key) }));
}

/** How many of the category's fields are still empty — drives the "N of M left" counter. */
export function countRemaining(
  definitions: ListingCategoryAttribute[],
  values: Record<string, string>,
): number {
  return definitions.filter(d => !values[d.attrKey]?.trim()).length;
}

export function ListingAttributeFields({
  definitions, values, aiFilledKeys, onChange, loading, errors,
}: Props) {
  // Skeleton rather than a spinner, and never a blocking one: the rest of the form stays
  // usable while the definitions request is in flight.
  if (loading) {
    return (
      <div data-testid="listing-attributes-loading" className="space-y-3">
        {[0, 1, 2].map(i => (
          <div key={i} className="animate-pulse">
            <div className="h-4 w-24 bg-gray-200 rounded mb-2" />
            <div className="h-10 w-full bg-gray-100 rounded" />
          </div>
        ))}
      </div>
    );
  }

  // A category with no attributes defined ("Other") renders nothing at all — not an empty
  // panel with a heading, which would read as a loading failure.
  if (definitions.length === 0) return null;

  const remaining = countRemaining(definitions, values);

  return (
    <div data-testid="listing-attributes-section" className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">Details for this category</h3>
        <span data-testid="listing-attributes-remaining" className="text-xs text-gray-500">
          {remaining === 0
            ? 'All filled'
            : `${remaining} of ${definitions.length} left`}
        </span>
      </div>

      {definitions.map(def => (
        <AttributeField
          key={def.attrKey}
          definition={def}
          value={values[def.attrKey] ?? ''}
          aiFilled={aiFilledKeys.has(def.attrKey)}
          error={errors?.[def.attrKey]}
          onChange={v => onChange(def.attrKey, v)}
        />
      ))}
    </div>
  );
}

function AttributeField({
  definition, value, aiFilled, error, onChange,
}: {
  definition: ListingCategoryAttribute;
  value: string;
  aiFilled: boolean;
  error?: string;
  onChange: (value: string) => void;
}) {
  const testId = `listing-attribute-${definition.attrKey}`;

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor={testId}>
        {definition.label}
        {definition.isRequired && <span className="text-red-500"> *</span>}
        {definition.unit && <span className="text-gray-400 font-normal"> ({definition.unit})</span>}
        {aiFilled && (
          <span data-testid={`${testId}-ai-badge`} className="ml-2 text-violet-600 font-normal text-xs">
            AI suggested — check this
          </span>
        )}
      </label>

      {renderControl(definition, value, onChange, testId)}

      {error && (
        <p data-testid={`${testId}-error`} className="mt-1 text-xs text-red-600">{error}</p>
      )}
    </div>
  );
}

function renderControl(
  definition: ListingCategoryAttribute,
  value: string,
  onChange: (value: string) => void,
  testId: string,
) {
  const baseClass =
    'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:outline-none';

  switch (definition.dataType) {
    case AttributeDataType.Enum:
      return (
        <select
          id={testId}
          data-testid={testId}
          className={baseClass}
          value={value}
          onChange={e => onChange(e.target.value)}
        >
          <option value="">Select…</option>
          {definition.options.map(option => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      );

    case AttributeDataType.Bool:
      return (
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            id={testId}
            data-testid={testId}
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300 text-emerald-600"
            checked={value === 'true'}
            onChange={e => onChange(e.target.checked ? 'true' : 'false')}
          />
          Yes
        </label>
      );

    case AttributeDataType.Number:
    case AttributeDataType.Year:
      return (
        <input
          id={testId}
          data-testid={testId}
          type="number"
          inputMode="numeric"
          className={baseClass}
          value={value}
          min={definition.minValue ?? undefined}
          max={definition.maxValue ?? undefined}
          placeholder={definition.unit ? `e.g. 58000 (${definition.unit})` : 'Enter a number'}
          onChange={e => onChange(e.target.value)}
        />
      );

    default:
      return (
        <input
          id={testId}
          data-testid={testId}
          type="text"
          className={baseClass}
          value={value}
          maxLength={500}
          onChange={e => onChange(e.target.value)}
        />
      );
  }
}